import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Toast } from './Toast';

// ToastContext value shape: { toast: { success, error, info }, dismiss }
const ToastContext = createContext(null);

export function ToastProvider({ children, defaultDuration = 4000 }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    const timers = timersRef.current;
    const t = timers.get(id);
    if (t) { clearTimeout(t); timers.delete(id); }
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const push = useCallback((toast) => {
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => [...prev, { id, ...toast }]);
    const duration = toast.duration ?? defaultDuration;
    if (duration !== 0 && Number.isFinite(duration)) {
      const handle = setTimeout(() => {
        timersRef.current.delete(id);
        setToasts((prev) => prev.filter((x) => x.id !== id));
      }, duration);
      timersRef.current.set(id, handle);
    }
    return id;
  }, [defaultDuration]);

  const api = useMemo(() => ({
    toast: {
      success: (msg, opts) => push({ tone: 'success', msg, ...opts }),
      error:   (msg, opts) => push({ tone: 'danger',  msg, ...opts }),
      info:    (msg, opts) => push({ tone: 'info',    msg, ...opts }),
    },
    dismiss,
  }), [push, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="lk-toast-region"
        role="region"
        aria-label="Уведомления"
        aria-live="polite"
        aria-relevant="additions"
      >
        {toasts.map((t) => (
          <Toast
            key={t.id}
            id={t.id}
            msg={t.msg}
            tone={t.tone}
            action={t.action}
            onDismiss={dismiss}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Safe fallback: return no-op stubs so unit-test renders without provider don't crash.
    // Real runtime mounts ToastProvider at the App root (see App.jsx).
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[useToast] called outside ToastProvider — returning no-op stubs');
    }
    return {
      success: () => undefined,
      error: () => undefined,
      info: () => undefined,
    };
  }
  return ctx.toast;
}

export default ToastProvider;
